import classNames from 'classnames';
import { Link } from 'react-router-dom';

import ErrorBlock from '../../../common/ErrorBlock';
import { useActiveVoucherCount } from '../../../hooks/queries/useActiveVoucherCount';
import { TicketIcon } from '../../../icons/TicketIcon';
import { formatError } from '../../../util/errors';
import { PATH_HUB_VOUCHERS } from '../../../util/pagePath';
import { BookOneOffSession } from './BookOneOffSession';

export interface VoucherWidgetProps {
  className?: string;
}

export const VoucherWidget = ({ className }: VoucherWidgetProps) => {
  const { voucherCount, vouchers, isLoading: isVoucherLoading, error: vouchersError } = useActiveVoucherCount();

  if (voucherCount === 0 && !isVoucherLoading) {
    return null;
  }

  return (
    <div
      className={classNames(
        'mb-10 flex w-full flex-col rounded-lg border border-slate-2 bg-white px-6 py-5 shadow-md lg:mb-8 lg:p-10 lg:py-8',
        className,
        {
          hidden: isVoucherLoading,
        }
      )}
    >
      {vouchersError && <ErrorBlock errorMsg={formatError(vouchersError)} />}
      {(vouchers || voucherCount > 0) && (
        <>
          <div className="flex w-full flex-row gap-3">
            <div className="flex !w-6 shrink-0 items-start justify-center">
              <TicketIcon width={24} height={24} className="mt-[2px] fill-blood-orange-4" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
                <div className="flex items-center gap-3 text-lg font-bold lg:gap-4">
                  <p className="text-lg font-bold">
                    {voucherCount} Cluey Voucher{voucherCount > 1 ? 's' : ''} available
                  </p>
                </div>
                <div className="flex w-full flex-col gap-2 lg:flex-row">
                  <span className="gap-y-1">
                    Use your voucher{voucherCount > 1 ? 's' : ''} to book sessions before they expire &nbsp;
                    <Link className="link hidden !text-base lg:inline-block" to={PATH_HUB_VOUCHERS}>
                      View vouchers
                    </Link>
                  </span>
                  <Link
                    className="link flex !text-base underline lg:hidden lg:justify-end lg:text-right"
                    to={PATH_HUB_VOUCHERS}
                  >
                    View vouchers
                  </Link>
                </div>
                <div className="hidden !text-sm">
                  <p>
                    Did you know you can use vouchers for any subject or student in your household?{' '}
                    <Link className="link pl-1" to={PATH_HUB_VOUCHERS}>
                      Start trial
                    </Link>
                  </p>
                </div>
              </div>
              <div className="hidden shrink-0 flex-col items-center gap-2 lg:flex">
                <BookOneOffSession
                  buttonClassName="rounded-full bg-primary px-4 py-2 text-blue-5 hover:text-white"
                  buttonAppearance="reverse"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex lg:hidden">
            <div className="flex w-full items-center">
              <BookOneOffSession
                buttonClassName="w-full rounded-full bg-primary px-4 py-2 text-blue-5 hover:text-white lg:w-auto"
                buttonAppearance="reverse"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
